import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable,
  throwError,
  BehaviorSubject,
  interval,
  Subscription,
} from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Empresa, EmpresaInit } from '../../_models/empresa.model';
import { CompanyInformationRisk, CompanyInformationRiskLastMonths, RiskRequest } from '../../_models/company.model';
import { SnackBarService } from '../../components/snack-bar/snackBar.service';
import { GtmService } from '../gtm/gtm.service';
import { EmpresaScmInfo } from '../../_views/create-supplier/create-supplier.model';
@Injectable({
  providedIn: 'root',
})
export class EmpresaService {
  private apiUrl = `${environment.urlApi}/empresas`;

  // Subject para el estado del polling
  private riskPollingSubject = new BehaviorSubject<{
    status: number;
    message: string;
  } | null>(null);
  riskPolling$ = this.riskPollingSubject.asObservable();

  private pollingSubscription?: Subscription;
  private readonly POLLING_INTERVAL = 3000; // 3 segundos

  constructor(
    private http: HttpClient,
    private snackBarService: SnackBarService,
    private gtmService: GtmService
  ) {}

  getEmpresaById(id: string): Observable<Empresa> {
    return this.http
      .get<Empresa>(`${this.apiUrl}/${id}`)
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error fetching empresa by id:', error);
          return throwError(() => new Error('Error fetching empresa'));
        })
      );
  }

  /**
   * Obtiene la información de riesgo de una empresa
   * @param subcontractorIdentificationNumber RUT de la empresa
   * @returns Observable con la información de riesgo
   */
  getInformationRisk(empresaId: string): Observable<CompanyInformationRisk> {
    return this.http
      .get<CompanyInformationRisk>(
        `${this.apiUrl}/informationrisk/${empresaId}`
      )
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error fetching risk information:', error);
          return throwError(() => new Error('Error fetching risk information'));
        })
      );
  }

  /**
   * Obtiene la información de riesgo de los últimos meses de una empresa
   * @param empresaId ID de la empresa
   * @returns Observable con la información de riesgo de los últimos meses
   */
  getInformationRiskLastMonths(empresaId: string): Observable<CompanyInformationRiskLastMonths[]> {
    return this.http
      .get<CompanyInformationRiskLastMonths[]>(
        `${this.apiUrl}/informationRiskLastMonths/${empresaId}`
      )
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error fetching risk information last months:', error);
          return throwError(() => new Error('Error fetching risk information last months'));
        })
      );
  }

  /**
   * Solicita información de riesgo para una empresa
   * @param empresaId ID de la empresa
   * @param userId ID del usuario
   * @param user Usuario que realiza la solicitud
   * @returns Observable con el resultado de la solicitud
   */
  getRiskInformationRequest(
    empresaId: string,
    userId: string,
    user: string
  ): Observable<boolean> {
    return this.http
      .get<boolean>(
        `${this.apiUrl}/${empresaId}/riskInformationRequest/${userId}/${user}`
      )
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error requesting risk information:', error);
          return throwError(() => new Error('Error requesting risk information'));
        })
      );
  }

  /**
   * Obtiene el estado de la solicitud de riesgo
   * @param empresaId ID de la empresa
   * @param companyId ID de la compañía
   * @returns Observable con el estado de la solicitud
   */
  getRiskRequest(
    empresaId: string,
    companyId: string
  ): Observable<RiskRequest> {
    return this.http
      .get<RiskRequest>(
        `${this.apiUrl}/${empresaId}/riskRequest/${companyId}`
      )
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error fetching risk request:', error);
          return throwError(() => new Error('Error fetching risk request'));
        })
      );
  }

  /**
   * Inicia el polling para verificar el estado del request de riesgo
   * @param empresaId ID de la empresa
   * @param companyId ID de la compañía
   */
  startRiskPolling(empresaId: string, companyId: string): void {
    // Cancelar cualquier polling existente
    this.stopRiskPolling();

    // Limpiar el estado anterior
    this.riskPollingSubject.next(null);

    this.pollingSubscription = interval(this.POLLING_INTERVAL)
      .pipe(
        switchMap(() => this.getRiskRequest(empresaId, companyId))
      )
      .subscribe({
        next: (latestRequest: RiskRequest) => {
          if (latestRequest.status !== 0) {
            this.stopRiskPolling();
            const message =
              latestRequest.status === 1
                ? 'La información de riesgo ha sido actualizada correctamente'
                : 'Hubo un error actualizando la calificación de riesgo';

            // Emitir el estado actual
            this.riskPollingSubject.next({
              status: latestRequest.status,
              message,
            });

            // Mostrar snackbar directamente desde el servicio
            this.snackBarService.displaySnackBar(
              latestRequest.status === 1 ? 'success' : 'error',
              latestRequest.status === 1 ? 'Actualización exitosa' : 'Error',
              message
            );

            if (latestRequest.status === 1) {
              // Evento GTM
              this.gtmService.push({
                dominio: 'empresa',
                accion: 'resultado_riesgo',
                interaccion: 'data',
                componente_ui: 'service',
                valor: latestRequest.status,
                resultado_accion: 'exitoso',
                rut_empresa: empresaId
              });
            }

            // Si fue exitoso, esperar un momento antes de limpiar el estado
            if (latestRequest.status === 1) {
              setTimeout(() => {
                this.riskPollingSubject.next(null);
              }, 100);
            } else {
              // Si hubo error, limpiar inmediatamente
              this.riskPollingSubject.next(null);
            }
          }
        },
        error: () => {
          this.stopRiskPolling();
          const message =
            'Hubo un error actualizando la calificación de riesgo';

          // Emitir el estado de error
          this.riskPollingSubject.next({
            status: 2,
            message,
          });

          // Mostrar snackbar de error
          this.snackBarService.displaySnackBar('error', 'Error', message);

          // Limpiar el estado después de mostrar la notificación
          this.riskPollingSubject.next(null);

          // Evento GTM
          this.gtmService.push({
            dominio: 'empresa',
            accion: 'resultado_riesgo',
            interaccion: 'data',
            componente_ui: 'service',
            valor: message,
            resultado_accion: 'fallido',
            rut_empresa: empresaId
          });
        },
      });
  }

  /**
   * Detiene el polling de riesgo
   */
  stopRiskPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
    // Limpiar el estado al detener el polling
    this.riskPollingSubject.next(null);
  }

   /**
    * Actualiza parcialmente la información de una empresa usando PATCH
    * Solo envía los campos modificados
    * @param id ID de la empresa
    * @param changes Objeto con los campos modificados
    * @returns Observable con la empresa actualizada
    */
   updateEmpresaPartial(id: string, changes: Record<string, any>): Observable<Empresa> {
     return this.http
       .patch<Empresa>(`${this.apiUrl}/${id}`, changes)
       .pipe(
         tap((updatedEmpresa) => {
           // Mostrar mensaje de éxito
           this.snackBarService.displaySnackBar(
             'success',
             'Éxito',
             'Empresa actualizada exitosamente'
           );

           // Evento GTM
           this.gtmService.push({
             dominio: 'empresa',
             accion: 'actualizar_parcial',
             componente_ui: 'empresa_service',
             valor: id,
             resultado_accion: 'exitoso'
           });
         }),
         catchError((error) => {
           console.error('Error updating empresa partially:', error);
           return throwError(() => new Error('Error updating empresa'));
         })
       );
   }

  /**
   * Actualiza la información de una empresa
   * @param id ID de la empresa
   * @param empresa Objeto completo de la empresa actualizada
   * @returns Observable con la empresa actualizada
   */
  updateEmpresa(id: string, empresa: Empresa): Observable<Empresa> {
    return this.http
      .put<Empresa>(`${this.apiUrl}/${id}`, empresa)
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error updating empresa:', error);
          return throwError(() => new Error('Error updating empresa'));
        })
      );
  }

  /**
   * Obtiene la información de empresa desde el WS get-info-empresa-scm
   * @param rut RUT de la empresa (formato: 12345678-9)
   * @returns Observable con la información de la empresa
   */
  getInfoEmpresaScm(rut: string): Observable<EmpresaScmInfo> {
    return this.http
      .get<EmpresaScmInfo>(`${this.apiUrl}/get-info-empresa-scm/${rut}`)
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.log('No se encontró información de empresa SCM para RUT:', rut);
          return throwError(() => new Error('NOT_FOUND'));
        })
      );
  }

  /**
   * Obtiene la información de empresa y sus roles
   * @param companyId  empresa
   * @param orgId  centro gestion
   * @param user  userName
   * @returns Observable con la información de rut, id y roles
   */
  initialize(companyId: string, orgId: string, user: string): Observable<EmpresaInit> {
    return this.http
      .get<EmpresaInit>(`${this.apiUrl}/initialize/${companyId}/${orgId}/${user}`)
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.log('No se encontró información de empresa para inicializar:', companyId);
          return throwError(() => new Error('NOT_FOUND'));
        })
      );
  }
}
